/**
 * Derives host-facing status labels for booking list rows.
 * Keeps all mapping logic out of JSX.
 */

export interface BookingListStatusInput {
  type: 'activity' | 'accommodation';
  status: string;
  /** Accommodation-only fields */
  paymentStatus?: string;
  amountPaidOre?: number;
  totalStayOre?: number | null;
  requiresPrepayment?: boolean;
  prepaymentAmountOre?: number | null;
}

/**
 * Map a BookingListStatus tone to a Badge variant used in detail pages.
 */
export function toneToBadgeVariant(tone: BookingListStatus['tone']): 'default' | 'destructive' | 'secondary' {
  if (tone === 'destructive') return 'destructive';
  if (tone === 'neutral') return 'secondary';
  return 'default';
}

export interface BookingListStatus {
  /** Primary host-facing label, e.g. "Delvis betald" */
  label: string;
  /** Optional compact payment summary, e.g. "800 / 1 800 kr" */
  paymentSummary: string | null;
  /** Visual tone for badge styling */
  tone: 'attention' | 'progress' | 'success' | 'neutral' | 'destructive';
}

function fmtKr(ore: number): string {
  return Math.round(ore / 100).toLocaleString('sv-SE') + ' kr';
}

export function getBookingListStatus(input: BookingListStatusInput): BookingListStatus {
  const { type, status } = input;

  // Activity bookings: simple status only
  if (type === 'activity') {
    return getActivityStatus(status);
  }

  return getAccommodationStatus(input);
}

function getActivityStatus(status: string): BookingListStatus {
  switch (status) {
    case 'pending':
      return { label: 'Förfrågan', paymentSummary: null, tone: 'attention' };
    case 'confirmed':
      return { label: 'Bekräftad', paymentSummary: null, tone: 'success' };
    case 'completed':
      return { label: 'Genomförd', paymentSummary: null, tone: 'neutral' };
    case 'cancelled':
      return { label: 'Avbokad', paymentSummary: null, tone: 'destructive' };
    default:
      return { label: status, paymentSummary: null, tone: 'neutral' };
  }
}

function getAccommodationStatus(input: BookingListStatusInput): BookingListStatus {
  const {
    status,
    paymentStatus = 'none',
    amountPaidOre = 0,
    totalStayOre,
    requiresPrepayment,
    prepaymentAmountOre,
  } = input;

  const totalOre = totalStayOre ?? 0;
  // Primary: verify via amounts when totalStayOre is known
  // Fallback: when totalStayOre is unknown (null/0), trust paymentStatus === 'paid'
  const isPaid = totalOre > 0
    ? amountPaidOre >= totalOre
    : paymentStatus === 'paid';
  const isPartiallyPaid = amountPaidOre > 0 && !isPaid;

  switch (status) {
    case 'pending':
      return { label: 'Förfrågan', paymentSummary: null, tone: 'attention' };

    case 'reserved_pending_payment': {
      const summary = requiresPrepayment && prepaymentAmountOre
        ? `${fmtKr(amountPaidOre)} / ${fmtKr(prepaymentAmountOre)}`
        : null;
      return {
        label: 'Reserverad · Inväntar betalning',
        paymentSummary: summary,
        tone: 'attention',
      };
    }

    case 'confirmed': {
      if (isPaid) {
        return {
          label: 'Betald',
          paymentSummary: totalOre > 0 ? fmtKr(amountPaidOre) : null,
          tone: 'success',
        };
      }
      if (isPartiallyPaid) {
        return {
          label: 'Delvis betald',
          paymentSummary: totalOre > 0 ? `${fmtKr(amountPaidOre)} / ${fmtKr(totalOre)}` : null,
          tone: 'progress',
        };
      }
      return {
        label: 'Bekräftad · Ej betald',
        paymentSummary: null,
        tone: 'progress',
      };
    }

    case 'completed':
      return { label: 'Genomförd', paymentSummary: null, tone: 'neutral' };

    case 'cancelled':
      return { label: 'Avbokad', paymentSummary: null, tone: 'destructive' };

    default:
      return { label: status, paymentSummary: null, tone: 'neutral' };
  }
}
